import { useState } from 'react';

import Btn from '../ui/Btn';
import { Spinner } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import { api } from '../../lib/api';
import { useIsMobile } from '../../lib/useBreakpoint';

/**
 * RetryClassificationButton — shown only when a doc's AI classification failed.
 *
 * Self-guards on `doc.classification_status === 'classification_failed'`, so
 * older docs that pre-date the migration (no classification_status field at
 * all) render NOTHING — callers don't need to wrap this in a conditional.
 *
 * Props:
 *   doc        — the document_uploads row
 *   teamId     — current workspace id (RetryClassificationButton can't read context;
 *                pass explicitly so the component works inside any panel)
 *   onUpdated  — (updatedDoc) => void. Called immediately with the optimistic
 *                'classifying' state, and again on retry success/failure.
 *   variant    — 'inline' (default — badge + button in a row),
 *                'callout' (vertical card with title + error + button, for FileViewerModal),
 *                'button-only' (no badge, just the retry button — for cell-style placements)
 *
 * Reuses api.classifyUploadThrottled so retries share the global classify
 * concurrency limit (max 2 in-flight / 250ms stagger).
 */
export default function RetryClassificationButton({ doc, teamId, onUpdated, variant = 'inline' }) {
  const isMobile = useIsMobile();
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(null);

  if (doc?.classification_status !== 'classification_failed') return null;

  const handleRetry = async () => {
    if (busy) return;
    setBusy(true);
    setLocalError(null);

    // Optimistic flip to 'classifying' so the failed badge disappears
    // immediately. Backend will set the real status (also 'classifying'
    // at start, then 'classified' or 'classification_failed' on outcome).
    const optimistic = {
      ...doc,
      classification_status: 'classifying',
      classification_error: null,
    };
    onUpdated?.(optimistic);

    try {
      await api.classifyUploadThrottled({
        team_id: teamId,
        member_id: doc.member_id,
        upload_id: doc.id,
        storage_path: doc.storage_path,
        suggested_template_id: doc.template_id || null,
      });
      // Backend resolves only after n8n finishes (or times out + writes status).
      // Either way, the DB now has the authoritative state — parents that
      // refresh on a poll/interval will pick it up automatically.
      setBusy(false);
    } catch (err) {
      console.warn('Retry classification failed:', err);
      const msg = err?.message || 'Retry failed';
      setLocalError(msg);
      // Revert the optimistic update — server-side may or may not have
      // written the failure; we surface the immediate error to the user.
      onUpdated?.({
        ...doc,
        classification_status: 'classification_failed',
        classification_error: msg,
      });
      setBusy(false);
    }
  };

  // Truncate the stored error to a humane preview length
  const errPreview = (doc.classification_error || '').slice(0, 40);
  const errTruncated = (doc.classification_error || '').length > 40;

  // ── Callout variant — full vertical card (FileViewerModal side panel) ──
  if (variant === 'callout') {
    return (
      <div style={{
        padding: 12,
        background: 'var(--warn-bg)',
        border: '1px solid rgba(245,158,11,0.28)',
        borderRadius: 'var(--r-md)',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon.alert size={14} stroke="var(--warn)" />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--warn)' }}>
            AI classification failed
          </span>
        </div>
        {doc.classification_error && (
          <div style={{ fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
            {doc.classification_error}
          </div>
        )}
        {localError && (
          <div style={{ fontSize: 11, color: 'var(--danger)', lineHeight: 1.4 }}>
            Retry failed: {localError}
          </div>
        )}
        <Btn
          variant="outline"
          size="sm"
          icon={busy ? null : Icon.refresh}
          onClick={handleRetry}
          disabled={busy}
          style={{ width: '100%', justifyContent: 'center' }}
        >
          {busy ? <><Spinner size={12} color="var(--text-2)" /> Retrying…</> : 'Retry classification'}
        </Btn>
      </div>
    );
  }

  // ── Button-only variant (desktop table Actions cell) ──
  if (variant === 'button-only') {
    return (
      <Btn
        variant="ghost"
        size="xs"
        icon={busy ? null : Icon.refresh}
        onClick={handleRetry}
        disabled={busy}
        title={`Retry classification${doc.classification_error ? ' — ' + doc.classification_error : ''}`}
        style={{ color: 'var(--warn)' }}
      >
        {busy ? <Spinner size={11} color="var(--warn)" /> : 'Retry'}
      </Btn>
    );
  }

  // ── Inline variant (default) — badge + button in a single row ──
  return (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      gap: isMobile ? 6 : 8,
      padding: isMobile ? '6px 8px' : '6px 10px',
      background: 'var(--warn-bg)',
      border: '1px solid rgba(245,158,11,0.22)',
      borderRadius: 8,
      fontSize: 11.5,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        flex: 1, minWidth: 0,
      }}>
        <Icon.alert size={11} stroke="var(--warn)" style={{ flexShrink: 0 }} />
        <span style={{
          fontWeight: 700, color: 'var(--warn)', flexShrink: 0,
        }}>
          Classification failed
        </span>
        {errPreview && (
          <span style={{
            color: 'var(--text-3)', minWidth: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            · {errPreview}{errTruncated ? '…' : ''}
          </span>
        )}
      </div>
      <Btn
        variant="ghost"
        size="xs"
        icon={busy ? null : Icon.refresh}
        onClick={handleRetry}
        disabled={busy}
        style={{
          color: 'var(--warn)',
          minHeight: 0,
          width: isMobile ? '100%' : 'auto',
          justifyContent: isMobile ? 'center' : 'flex-start',
        }}
      >
        {busy ? <><Spinner size={11} color="var(--warn)" /> Retrying…</> : 'Retry'}
      </Btn>
    </div>
  );
}
